import { isEmpty, uniq } from 'lodash';
import { adminOrganizations } from '../constants/constants';

// INPUT: An array of organization objects and the current Org's name
// OUTPUT: An array containing the tree of organizations, each Org has its children in an array as an added property
export const buildOrganizationTree = (arr, topOrgName) => {
  var rootElements = [];
  var hashTable = {};

  // Use organization ID as key to prevent duplicates from same-named orgs
  arr &&
    arr.forEach((org) => {
      hashTable[org.id] = { ...org };
    });

  arr &&
    arr.forEach((org) => {
      const lastParent = org.parentOrg[org.parentOrg.length - 1];
      let parentId = lastParent ? lastParent.id : null;
      
      // If this is the top organization, treat it as root
      if (org.name === topOrgName) parentId = null;
      
      if (parentId != null) {
        const parentObject = hashTable[parentId];
        if (parentObject) {
          if (parentObject.hasOwnProperty('children')) {
            // Prevent duplicate children by checking ID
            const childExists = parentObject.children.some(child => child.id === org.id);
            if (!childExists) {
              parentObject.children.push(hashTable[org.id]);
            }
          } else {
            parentObject.children = [hashTable[org.id]];
          }
        } else {
          // Parent not found in current dataset, treat as root
          rootElements.push(hashTable[org.id]);
        }
      } else {
        rootElements.push(hashTable[org.id]);
      }
    });

  return rootElements;
};

// Adds keys of an ANT Table in a tree fashion
export const buildAntTree = (nodes, parentKey) => {
  // Add cycle detection to prevent infinite recursion
  const visited = new Set();
  
  const buildTree = (nodes, parentKey, depth = 0) => {
    // Safety check: prevent infinite recursion
    if (depth > 100) {
      console.warn('buildAntTree: Maximum depth exceeded, possible circular reference');
      return nodes.map((node, i) => ({ ...node, key: `${parentKey}${i}-maxdepth` }));
    }
    
    return nodes.map((node, i) => {
      const key = `${parentKey}${i}`;
      
      // Check for circular reference
      if (visited.has(node.id)) {
        console.warn('buildAntTree: Circular reference detected for node:', node.id);
        return { ...node, key: `${key}-circular` };
      }
      
      visited.add(node.id);
      
      const result = node.children
        ? {
            ...node,
            children: buildTree(node.children, key, depth + 1),
            key,
          }
        : { ...node, key };
      
      visited.delete(node.id);
      return result;
    });
  };
  
  return buildTree(nodes, parentKey);
};

// INPUT: An array of roles (as objects) from an Org or User
// OUTPUT: Hashtable of all roles' combined features with their respective permissions as strings in an array
export const permissionsFromRoles = (roles) => {
  const permissions = {};
  roles.forEach((role) => {
    if (!role) return;
    role.roleItems.forEach((item) => {
      if (permissions[item.feature]) {
        item.actions.forEach((action) => {
          if (!permissions[item.feature].includes(action)) {
            permissions[item.feature].push(action);
          }
        });
      } else {
        permissions[item.feature] = item.actions;
      }
    });
  });
  return permissions;
};

// This function grabs the parent-most Role on a User. This might eventually be set as a "default org" on the user.
export const getTopRole = (roles) => {
  if (isEmpty(roles)) return null;
  let top = roles[0];
  roles.forEach((role) => {
    if (role.org.parentOrg.length < top.org.parentOrg.length) top = role;
  });
  return top;
};

/**
 * Checks if a set of roles contains an administrator role
 * @param {array} roles The roles contained on a User
 */
export const isAdminOrg = (roles) =>
  roles.some((role) => adminOrganizations.some((orgName) => orgName === role.org.name));

/**
 * Get features that are enabled across all roles on a User
 * @param {array} roles The roles contained on a User
 */
export const getRolesFeatures = (roles) => {
  let features = [];
  roles.forEach((role) => {
    role.roleItems.forEach((item) => {
      if (features.includes(item)) {
        return;
      }
      if (item.actions.includes('READ')) features.push(item.feature);
    });
  });
  return uniq(features);
};
